<template>
  <div>
    <v-toolbar color="page-toolbar">
      <v-toolbar-title class="text-body-large"> Caméras <small>(Administration)</small> </v-toolbar-title>
    </v-toolbar>

    <v-container fluid class="pa-4">
      <v-row>
        <!-- Spypoint account of the extract -->
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="h-100">
            <v-card-title class="d-flex align-center ga-2">
              <v-icon color="primary">mdi-account-key-outline</v-icon>
              Compte Spypoint
            </v-card-title>
            <v-card-text>
              <p class="text-body-medium mb-1">
                <template v-if="status.username">
                  Compte utilisé : <strong>{{ status.username }}</strong>
                </template>
                <template v-else>Aucun compte configuré : l'extraction ne peut pas se connecter.</template>
              </p>
              <p v-if="status.credentials_updated_at" class="text-body-small text-medium-emphasis mb-4">
                Mot de passe enregistré le {{ formatDate(status.credentials_updated_at) }}
              </p>
              <p class="text-body-small text-medium-emphasis mb-4">
                Les caméras doivent être partagées avec ce compte (onglet « Partagées avec moi » de l'application
                Spypoint). Le mot de passe est vérifié auprès de Spypoint avant d'être enregistré et n'est jamais
                affiché.
              </p>
              <v-form ref="credentialsForm" @submit.prevent="saveCredentials">
                <v-text-field
                  v-model="username"
                  label="Courriel du compte Spypoint"
                  type="email"
                  autocomplete="off"
                  density="compact"
                  class="mb-2"
                ></v-text-field>
                <v-text-field
                  v-model="password"
                  label="Mot de passe"
                  :type="showPassword ? 'text' : 'password'"
                  :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  autocomplete="new-password"
                  density="compact"
                  class="mb-2"
                  @click:append-inner="showPassword = !showPassword"
                ></v-text-field>
                <v-alert v-if="credentialsError" type="error" variant="tonal" density="compact" class="mb-2">
                  {{ credentialsError }}
                </v-alert>
                <v-btn
                  type="submit"
                  color="primary"
                  prepend-icon="mdi-content-save-outline"
                  :loading="savingCredentials"
                  :disabled="!username || !password"
                >
                  Vérifier et enregistrer
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Last run of the hourly extract -->
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="h-100">
            <v-card-title class="d-flex align-center ga-2">
              <v-icon color="primary">mdi-sync</v-icon>
              Extraction
            </v-card-title>
            <v-card-text>
              <p class="text-body-medium mb-1">
                Les 6 dernières photos de chaque caméra sont copiées toutes les heures.
              </p>
              <p class="text-body-small text-medium-emphasis mb-4">
                Dernière extraction :
                {{ status.last_run_at ? formatDate(status.last_run_at) : "jamais" }}
                <template v-if="status.cameras_count !== undefined">
                  · {{ status.cameras_count }} caméra{{ status.cameras_count > 1 ? "s" : "" }}
                  <template v-if="status.cameras_in_error"> · {{ status.cameras_in_error }} en erreur</template>
                </template>
              </p>
              <v-alert v-if="status.error" type="error" variant="tonal" density="compact" class="mb-4">
                {{ status.error }}
              </v-alert>
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-play"
                :loading="running"
                :disabled="!status.username"
                @click="runExtract"
              >
                Lancer l'extraction
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card variant="outlined" class="mt-4">
        <v-card-title>Caméras partagées</v-card-title>
        <v-data-table
          :headers="headers"
          :items="cameras"
          :loading="loading"
          :items-per-page="-1"
          hide-default-footer
          no-data-text="Aucune caméra : configurez le compte Spypoint puis lancez l'extraction."
        >
          <template #[`item.photo`]="{ item }">
            <div class="camera-thumb my-1">
              <img v-if="latestPhoto(item)" :src="latestPhoto(item).url" :alt="item.name" loading="lazy" />
              <v-icon v-else color="icon-inactive">mdi-image-off-outline</v-icon>
            </div>
          </template>
          <template #[`item.name`]="{ item }">
            <div class="font-weight-medium">{{ item.name }}</div>
            <div class="text-body-small text-medium-emphasis">{{ item.model }} · {{ item.owner_name }}</div>
          </template>
          <template #[`item.last_photo_at`]="{ item }">
            <template v-if="item.last_photo_at">
              <div>{{ formatDate(item.last_photo_at) }}</div>
              <v-chip v-if="isStale(item.last_photo_at)" size="x-small" color="warning" variant="tonal" label>
                {{ formatAge(item.last_photo_at) }}
              </v-chip>
              <span v-else class="text-body-small text-medium-emphasis">{{ formatAge(item.last_photo_at) }}</span>
            </template>
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template #[`item.last_extract_at`]="{ item }">
            {{ item.last_extract_at ? formatDate(item.last_extract_at) : "—" }}
          </template>
          <template #[`item.state`]="{ item }">
            <div v-if="item.error" class="d-flex align-start ga-1 text-error text-body-small camera-error">
              <v-icon size="small" color="error">mdi-alert-circle-outline</v-icon>
              <span>{{ item.error }}</span>
            </div>
            <v-chip v-else size="small" color="success" variant="tonal" label prepend-icon="mdi-check">OK</v-chip>
          </template>
          <template #[`item.sections`]="{ item }">
            <div v-if="sectionsByCamera[item.id]" class="d-flex flex-wrap ga-1">
              <v-chip
                v-for="section in sectionsByCamera[item.id]"
                :key="section.id"
                size="x-small"
                label
                :color="section.camera_info ? 'primary' : undefined"
                :title="section.camera_info ? 'Onglet Caméra activé' : 'Onglet Caméra désactivé'"
              >
                {{ section.river }}<span v-if="section.name">&nbsp;— {{ section.name }}</span>
              </v-chip>
            </div>
            <span v-else class="text-medium-emphasis">Aucune</span>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
  import SectionsService from "@/store/modules/sections";
  import { CamerasService } from "@/services/Cameras.service";
  import { notify } from "@/services/Notification.service";
  import { formatPhotoAge, formatPhotoDate, isStalePhoto } from "@/utils/cameras";

  export default {
    name: "CameraList",
    data() {
      return {
        cameras: [],
        sections: [],
        status: {},
        loading: true,
        running: false,
        username: "",
        password: "",
        showPassword: false,
        savingCredentials: false,
        credentialsError: "",
        headers: [
          { title: "", key: "photo", sortable: false, width: 96 },
          { title: "Caméra", key: "name" },
          { title: "Dernière photo", key: "last_photo_at" },
          { title: "Dernière extraction", key: "last_extract_at" },
          { title: "État", key: "state", sortable: false },
          { title: "Sections", key: "sections", sortable: false },
        ],
      };
    },
    computed: {
      sectionsByCamera() {
        const byCamera = {};
        for (const section of this.sections) {
          if (section.camera_id) {
            (byCamera[section.camera_id] = byCamera[section.camera_id] || []).push(section);
          }
        }
        return byCamera;
      },
    },
    async created() {
      await this.load();
    },
    methods: {
      async load() {
        this.loading = true;
        try {
          const [cameras, sections, status] = await Promise.all([
            CamerasService.listCameras(),
            SectionsService.actions.getSections(),
            CamerasService.getExtractStatus(),
          ]);
          this.cameras = cameras;
          this.sections = sections || [];
          this.status = status;
          if (!this.username) {
            this.username = status.username || "";
          }
        } catch (error) {
          console.error("Error loading cameras:", error);
        } finally {
          this.loading = false;
        }
      },
      async saveCredentials() {
        this.savingCredentials = true;
        this.credentialsError = "";
        try {
          const result = await CamerasService.setCredentials(this.username, this.password);
          this.password = "";
          notify(`Compte Spypoint enregistré : ${result.cameras} caméra(s) partagée(s) trouvée(s).`);
          await this.load();
        } catch (error) {
          this.credentialsError = error.message || "Le compte n'a pas pu être enregistré.";
        } finally {
          this.savingCredentials = false;
        }
      },
      async runExtract() {
        this.running = true;
        try {
          const result = await CamerasService.runExtract();
          notify(
            result.errors
              ? `Extraction terminée : ${result.errors} erreur(s), voir le détail ci-dessous.`
              : `Extraction terminée : ${result.cameras} caméra(s) à jour.`,
            result.errors ? "warning" : "success",
          );
        } catch (error) {
          notify(error.message || "L'extraction a échoué.", "error");
        } finally {
          this.running = false;
          await this.load();
        }
      },
      latestPhoto(camera) {
        return (camera.photos || [])[0] || null;
      },
      formatDate: formatPhotoDate,
      formatAge(value) {
        return formatPhotoAge(value);
      },
      isStale(value) {
        return isStalePhoto(value);
      },
    },
  };
</script>

<style scoped>
  .camera-thumb {
    width: 80px;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgb(var(--v-theme-surface-variant));
  }

  .camera-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .camera-error {
    max-width: 320px;
  }
</style>
